/**
 * @fileoverview Mock backend for the Sales Performance endpoints.
 * @description Registers GET /sales/performance/summary, /ranking and /pending and POST
 * /sales/performance/export on the feature's shared mock adapter, answering in the backend's
 * envelopes with its error codes (Confluence 3.5.11.36-.39, decision record
 * docs/decisions/2026-11-01-sales-performance.md in the backend repo): 400/0001 for a bad or
 * not-yet-started period or team key, 403/4130 when a Manager or Executive sends a team (even ALL),
 * 404/4131 for a team that names nobody selectable (see sales-performance-mock-selection.ts). The
 * figures come from sales-performance-mock-model.ts, projected from the shared stores; the export
 * reply is in sales-performance-export-mock.ts. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'

import { okEnvelope, queryOf } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { performanceExportReply } from './sales-performance-export-mock'
import {
  monthsOf,
  peopleInScope,
  percentOf,
  PERFORMANCE_LIST_CAP,
  pendingStatusOf,
  periodOf,
  roleCodeOf,
  scopeOf,
  targetsFor,
} from './sales-performance-mock-model'
import {
  isReply,
  PERIOD_CODES,
  rankedPeople,
  selectionOf,
} from './sales-performance-mock-selection'
import { listSalesRoster } from './sales-roster-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { PerformanceTarget } from './sales-performance-mock-model'
import type { Selection } from './sales-performance-mock-selection'
import type {
  SalesPerformancePeriodCode,
  SalesPerformanceTeamLine,
  SalesPerformanceTeamOption,
} from '@/features/sales/types/sales-performance.types'
import type { AxiosRequestConfig } from 'axios'

/**
 * @description Counts targets by the month they count in.
 * @param {PerformanceTarget[]} targets - The targets.
 * @param {string[]} months - The period's months.
 * @param {(item: PerformanceTarget) => boolean} counts - Which targets to count.
 * @returns {Record<string, number>} The count per month.
 */
function perMonth(
  targets: PerformanceTarget[],
  months: string[],
  counts: (item: PerformanceTarget) => boolean,
): Record<string, number> {
  return Object.fromEntries(
    months.map((month) => [
      month,
      targets.filter((item) => counts(item) && item.date.startsWith(month)).length,
    ]),
  )
}

/**
 * @description The comparison lines: one per Head or Manager in the selection, with their
 * downline's figures.
 * @param {Selection} selection - The validated selection.
 * @param {PerformanceTarget[]} targets - The selection's targets.
 * @param {string[]} months - The period's months.
 * @returns {SalesPerformanceTeamLine[]} The team lines.
 */
function teamLinesOf(
  selection: Selection,
  targets: PerformanceTarget[],
  months: string[],
): SalesPerformanceTeamLine[] {
  const roster = listSalesRoster()
  return selection.people
    .filter((user) => user.role === 'Sales Head' || user.role === 'Sales Manager')
    .map((lead) => {
      const ids = new Set(resolveDownlineUserIds(lead.id, roster))
      const own = targets.filter((item) => ids.has(item.target.ownerId))
      const achieved = own.filter((item) => item.achieved).length
      const byMonth = perMonth(own, months, (item) => item.achieved)
      return {
        teamKey: `TEAM:${lead.id}`,
        label: `${lead.name}'s team`,
        assigned: own.length,
        achieved,
        achievementPercent: percentOf(achieved, own.length),
        months: months.map((month) => ({ month, achieved: byMonth[month] ?? 0 })),
      }
    })
}

/**
 * @description The Team filter's choices, offered only to a caller who may filter.
 * @param {Selection} selection - The validated selection (its caller decides the answer).
 * @returns {SalesPerformanceTeamOption[]} The options, or none.
 */
function teamOptionsOf(selection: Selection): SalesPerformanceTeamOption[] {
  if (!scopeOf(selection.caller).canFilterTeam) return []
  return peopleInScope(selection.caller).flatMap((user) => {
    const lead = user.role !== 'Sales Executive'
    const alone: SalesPerformanceTeamOption = {
      key: `USER:${user.id}`,
      label: lead ? `${user.name} (direct)` : user.name,
      kind: 'USER',
    }
    return lead
      ? [{ key: `TEAM:${user.id}`, label: `${user.name}'s team`, kind: 'TEAM' as const }, alone]
      : [alone]
  })
}

/**
 * @description The periods a caller can pick: the year to date and every quarter that has begun.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {{ code: SalesPerformancePeriodCode; label: string }[]} The options.
 */
function periodOptionsOf(today: string) {
  return (PERIOD_CODES as SalesPerformancePeriodCode[]).flatMap((code) => {
    const { period, started } = periodOf(code, today)
    return started ? [{ code, label: period.label }] : []
  })
}

/**
 * @description Answers Get Sales Performance Summary.
 * @param {AxiosRequestConfig} config - The request config, for `period` and `team`.
 * @returns {MockReply} The KPIs, series, team lines and filter options.
 */
export function performanceSummaryReply(config: AxiosRequestConfig): MockReply {
  const selection = selectionOf(queryOf(config))
  if (isReply(selection)) return selection
  const { period, caller, people, today } = selection
  const ids = new Set(people.map((user) => user.id))
  const targets = targetsFor(ids, period, true)
  const inPeriod = targets.filter((item) => item.date >= period.startDate)
  const assigned = inPeriod.length
  const achieved = inPeriod.filter((item) => item.achieved).length
  const months = monthsOf(period, today)
  const achievedBy = perMonth(inPeriod, months, (item) => item.achieved)
  const onboardedBy = perMonth(inPeriod, months, (item) => item.onboarded)
  const watched = targets.filter(
    (item) =>
      !item.achieved && ['OVERDUE', 'AT_RISK'].includes(pendingStatusOf(item.target, today)),
  )
  return [
    200,
    okEnvelope(
      {
        period,
        scope: scopeOf(caller),
        kpis: {
          assigned,
          achieved,
          achievementPercent: percentOf(achieved, assigned),
          atRiskOrOverdue: watched.length,
        },
        months: months.map((month) => ({
          month,
          achieved: achievedBy[month] ?? 0,
          onboarded: onboardedBy[month] ?? 0,
        })),
        teams: teamLinesOf(selection, inPeriod, months),
        teamOptions: teamOptionsOf(selection),
        periodOptions: periodOptionsOf(today),
        hasData: targets.length > 0,
      },
      { evaluatedAt: new Date().toISOString() },
    ),
  ]
}

/**
 * @description Answers List Sales Performance Ranking.
 * @param {AxiosRequestConfig} config - The request config, for `period` and `team`.
 * @returns {MockReply} The ranking, capped, with the total before the cap.
 */
export function performanceRankingReply(config: AxiosRequestConfig): MockReply {
  const selection = selectionOf(queryOf(config))
  if (isReply(selection)) return selection
  const ranked = rankedPeople(selection)
  const items = ranked
    .slice(0, PERFORMANCE_LIST_CAP)
    .map(({ user, assigned, achieved }, index) => ({
      rank: index + 1,
      userId: user.id,
      name: user.name,
      role: roleCodeOf(user),
      assigned,
      achieved,
      achievementPercent: percentOf(achieved, assigned),
    }))
  return [
    200,
    okEnvelope({ items, total: ranked.length }, { evaluatedAt: new Date().toISOString() }),
  ]
}

const PENDING_ORDER = { OVERDUE: 0, AT_RISK: 1, ON_TRACK: 2, ON_HOLD: 3 }

/**
 * @description Answers List Sales Performance Pending Targets.
 * @param {AxiosRequestConfig} config - The request config, for `period` and `team`.
 * @returns {MockReply} The open targets, ordered and capped, with the totals before the cap.
 */
export function performancePendingReply(config: AxiosRequestConfig): MockReply {
  const selection = selectionOf(queryOf(config))
  if (isReply(selection)) return selection
  const { period, people, today } = selection
  const rows = targetsFor(new Set(people.map((user) => user.id)), period, true)
    .filter((item) => !item.achieved && item.target.stage !== 'Lost')
    .map((item) => ({
      targetId: item.target.id,
      bank: item.tenant.name,
      tenantCode: item.tenant.id,
      owner: {
        userId: item.target.ownerId,
        name: people.find((user) => user.id === item.target.ownerId)?.name ?? '',
      },
      dueDate: item.target.endDate,
      status: pendingStatusOf(item.target, today),
    }))
    .sort(
      (a, b) =>
        PENDING_ORDER[a.status] - PENDING_ORDER[b.status] ||
        a.dueDate.localeCompare(b.dueDate) ||
        a.bank.localeCompare(b.bank),
    )
  return [
    200,
    okEnvelope(
      {
        items: rows.slice(0, PERFORMANCE_LIST_CAP),
        total: rows.length,
        overdueCount: rows.filter((row) => row.status === 'OVERDUE').length,
      },
      { evaluatedAt: new Date().toISOString() },
    ),
  ]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/performance/summary').reply(performanceSummaryReply)
  salesMockAdapter.onGet('/sales/performance/ranking').reply(performanceRankingReply)
  salesMockAdapter.onGet('/sales/performance/pending').reply(performancePendingReply)
  salesMockAdapter.onPost('/sales/performance/export').reply(performanceExportReply)
}
